<template>
  <div>
    <md-list-item @click="recoverFactor" v-if="!tfaState">
      <span class="settings__item-text text_large">{{ i18n.lbl_recover_2fa() }}</span>
      <md-icon class="md-icon--half-sized">keyboard_arrow_right</md-icon>
    </md-list-item>
    <md-list-item @click="createFactor" v-else>
      <span class="settings__item-text text_large">{{ i18n.lbl_add_2fa() }}</span>
      <md-icon class="md-icon--half-sized">keyboard_arrow_right</md-icon>
    </md-list-item>

    <md-dialog :md-active.sync="isSettingsOpened">
      <md-dialog-title>{{ i18n.set_tfa_enable() }}</md-dialog-title>

      <div class="app__dialog-inner">
        <p class="tfa-settings__explain">{{ i18n.set_tfa_scan_the_qr_code() }}</p>
        <div class="tfa-settings__qr-outer" v-if="inputMode === INPUT_MODES.qr">
          <qrcode class="tfa-settings__qr-code"
                  :text="factor.qr"
                  :size="225"
                  color="#3f4244"
          />
        </div>
        <p class="tfa-settings__explain-additional">{{ i18n.set_or_manually_enter() }}</p>
        <clipboard-field
            class="tfa-settings__copy-secret"
            :value="factor.secret"
            :label="i18n.lbl_secret()"
        />
      </div>

      <md-dialog-actions class="md-layout md-alignment-center-right">
        <md-button @click="isSettingsOpened = false" class="md-primary">{{ i18n.lbl_cancel() }}</md-button>
        <md-button @click="updateFactor" class="md-primary">{{ i18n.lbl_confirm() }}</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>

</template>

<script>
  import ClipboardField from '@com/fields/ClipboardField'

  import {createTfaRecoveryDialog} from '@js/modals/tfa_recovery.modal'
  import { EventDispatcher } from '@js/events/event_dispatcher'
  import { factorsService } from '@api/factors.service'
  import { i18n } from '@js/i18n'

  import Qrcode from 'vue-qrcode-component'
  import { mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'

  const ENABLED_FACTOR_PRIORITY = 1
  const TFA_STATES = {
    on: false,
    off: true
  }
  const INPUT_MODES = {
    qr: 'qr',
    manual: 'manual'
  }

  export default {
    name: 'tfa-settings',
    components: { Qrcode, ClipboardField },
    props: ['tfaRecoveryInit'],
    data: _ => ({
      tfaState: TFA_STATES.off,
      inputMode: INPUT_MODES.qr,
      isSettingsOpened: false,
      factor: {
        id: -1,
        secret: null,
        qr: null
      },
      i18n,
      INPUT_MODES
    }),
    created () {
      this.checkState()
      if (this.tfaRecoveryInit) {
        this.recoverFactor()
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountBlocked,
        vuexTypes.accountBlockedReasons,
        vuexTypes.factorData,
        vuexTypes.walletId
      ])
    },
    methods: {
      async checkState () {
        let factors
        try {
          factors = (await factorsService.loadFactors()).data().filter(factor => factor.type === 'totp')
        } catch (error) {
          console.error(error)
          EventDispatcher.dispatchShowErrorEvent(i18n.unexpected_error())
          return
        }

        if (!factors.length) {
          return
        }

        const disabledFactors = factors
          .filter(factor => factor.attributes.priority === 0)
          .map(factor => factor.id)

        if (disabledFactors.length) {
          await Promise.all(disabledFactors.map(id => factorsService.deleteFactor(id)))
          return
        }

        this.tfaState = TFA_STATES.on
        this.factor.id = factors[0].id
      },
      async createFactor () {
        try {
          const factor = await factorsService.createFactor('totp')
          this.factor.secret = factor.attribute('secret')
          this.factor.qr = factor.attribute('seed')
          this.factor.id = factor.data('id')
          this.isSettingsOpened = true
        } catch (error) {
          console.error(error)
          if (error.showBanner) {
            error.showBanner(i18n.unexpected_error())
            return
          }
          EventDispatcher.dispatchShowErrorEvent(i18n.unexpected_error())
        }
      },
      async updateFactor () {
        try {
          await factorsService.updateFactor(
            this.factor.id,
            ENABLED_FACTOR_PRIORITY
          )
          this.tfaState = TFA_STATES.on
          this.isSettingsOpened = false
        } catch (error) {
          if (error.showBanner) {
            error.showBanner(i18n.unexpected_error())
            return
          }
          EventDispatcher.dispatchShowErrorEvent(i18n.unexpected_error())
        }
      },
      async recoverFactor () {
        if ((this.accountBlockedReasons.length === 1 && this.accountBlockedReasons[0].name === 'BlockReasonsTfaOtpRecovery') || !this.accountBlockedReasons.length) {
          try {
            factorsService.lostFactor(this.walletId)
            await createTfaRecoveryDialog(null, {walletId: this.walletId})
            EventDispatcher.dispatchShowSuccessEvent(i18n.tfa_recovery_unblocked())
            await this.createFactor()
          } catch (err) {
            EventDispatcher.dispatchShowErrorEvent(i18n.unexpected_error())
          }
        } else {
          EventDispatcher.dispatchShowErrorEvent(i18n.unexpected_error())
        }
      }
    }
  }
</script>
<style lang="scss">
  .roi-switch {
    &.md-theme-default {
      &.md-checked {
        &.md-primary {
          .md-switch-container {
            background-color: #FFFFFF;
            border: 1px solid rgba(0, 194, 176, 0.5);
            .md-switch-thumb {
              background: #00C2B0;
            }
          }
        }
      }
      .md-switch-container {
        width: 35px;
        height: 20px;
        background-color: #FFFFFF;
        border: 1px solid rgba(0, 0, 0, 0.1);
        box-sizing: border-box;
        border-radius: 30px;
        .md-switch-thumb {
          background: #E5E5E5;
          box-shadow: none;
          width: 20px;
          height: 20px;
        }
      }
    }
  }
</style>
<style lang="scss" scoped>
  @import './settings.scss';

  .md-dialog {
    width: 25rem;
  }

  .tfa-settings__qr-outer {
    max-width: 225px;
    margin: 0 auto 20px;
  }

  .tfa-settings__explain {
    margin-bottom: 20px;
  }

  .tfa-settings__explain-additional,
  .tfa-settings__copy-secret {
    margin-bottom: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/settings/security/Security.TfaSettings.vue